import { Navigate } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import { useListQuery } from "@shared-core/domain/useListQuery";
import type { CustomerSession } from "@shared-core/data/customer-api-types";
import {
  PortalHomeView,
  PORTAL_HOME_SHORTCUT_ICONS,
  type PortalHomeData,
  type PortalHomeShortcut,
} from "./home/PortalHomeView";
import { usePortalTheme } from "./theme/PortalThemeContext";
import { usePortalViewer } from "./PortalViewerContext";

/**
 * The shortcuts on the banner: the things a buyer comes to do. Ordering and spend follow the session's own facts, as
 * the nav does (a shortcut that is refused would be worse than none); with no session (the staff preview) all show.
 */
export function portalHomeShortcuts(
  session: CustomerSession | null,
): PortalHomeShortcut[] {
  const shortcuts: (PortalHomeShortcut & { show: boolean })[] = [
    {
      label: "Place an order",
      to: paths.portalCatalogue(),
      icon: PORTAL_HOME_SHORTCUT_ICONS.catalogue,
      show: session === null || session.can_order_by_card,
    },
    {
      label: "Track my orders",
      to: paths.portalOrders(),
      icon: PORTAL_HOME_SHORTCUT_ICONS.orders,
      show: true,
    },
    {
      label: "Check artwork",
      to: paths.portalArtworkCheck(),
      icon: PORTAL_HOME_SHORTCUT_ICONS.artwork,
      show: true,
    },
    {
      label: "See my spend",
      to: paths.portalSpend(),
      icon: PORTAL_HOME_SHORTCUT_ICONS.spend,
      show: session === null || session.can_see_spend,
    },
  ];
  return shortcuts.filter((s) => s.show).map(({ show: _show, ...s }) => s);
}

/**
 * The portal's home page (docs/features/portal-theme), the index route. It is the home only where the theme read
 * succeeded; otherwise the portal opens on Orders, as it did before themes.
 */
export function PortalHomePage() {
  const themed = usePortalTheme();
  const adapter = useCustomerDataAdapter();
  const { viewKey, sessionState, buildPortalUrl } = usePortalViewer();
  const hasTheme = Boolean(themed?.theme);
  const { state, retry } = useListQuery(
    () => (hasTheme ? adapter.getCustomerHome() : Promise.resolve(null)),
    [adapter, viewKey, hasTheme],
  );
  if (themed?.isLoading)
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Loading…
      </p>
    );
  if (!themed?.theme)
    return <Navigate to={buildPortalUrl(paths.portalOrders())} replace />;

  const data: PortalHomeData =
    state.status === "loading" ||
    (state.status === "success" && state.data === null)
      ? { status: "loading" }
      : state.status === "error"
        ? { status: "error", onRetry: retry }
        : { status: "ready", home: state.data! };
  return (
    <PortalHomeView
      theme={themed.theme.values}
      shortcuts={portalHomeShortcuts(
        sessionState.status === "success" ? sessionState.data : null,
      )}
      data={data}
      linkTo={buildPortalUrl}
    />
  );
}
